/** @fileoverview Coordinates the Fabrik home and active assistant conversation. */

import type { FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { toast } from '../components/ui/toast';
import ChatComposerPanel, { ImplementationChecklist } from '../components/AIChat/ChatComposerPanel';
import ChatComposer from '../components/AIChat/ChatComposer';
import ChatConversation from '../components/AIChat/ChatConversation';
import ChatHeader from '../components/AIChat/ChatHeader';
import ChatHistory from '../components/AIChat/ChatHistory';
import ChatWelcome from '../components/AIChat/ChatWelcome';
import FeedbackDialog from '../components/AIChat/FeedbackDialog';
import FabrikHome from '../components/FabrikHome';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  isChatAttentionSoundEnabled,
  playChatAttentionSound,
  unlockChatAttentionSound,
} from '../lib/chatAttentionSound';
import { useChat } from '../hooks/useChat';
import { useAttachments } from '../hooks/useAttachments';
import { useConversationFiles } from '../hooks/useConversationFiles';
import type { AskQuestionAnswer, Conversation } from '../types/chat';
import {
  boundSelection,
  requestPreviewSnapshot,
  type PreviewSelectionItem,
} from '../lib/previewSelection';
import type { ChatPageProps } from '../components/AIChat/types';
import {
  approveDiscovery,
  approveDiscoveryScopes,
  approvePlan,
  getPlan,
  getPlanAtRevision,
} from '../services/planApi';
import type { Plan, PlanState } from '../services/planApi';

type AttentionActivity = 'waiting-for-input' | 'completed-unvisited';

const workflowBlockerLabels: Record<string, string> = {
  overview: 'Add an app overview',
  goalUsers: 'Describe who the app is for',
  capabilitiesWorkflows: 'Add its key capabilities and workflows',
  implementationSteps: 'Add implementation steps and acceptance criteria',
  blockingQuestions: 'Resolve the plan’s blocking questions',
  discoveryFindings: 'Record at least one discovery finding and its source',
};

export default function ChatPage({
  tenant,
  user,
  appId,
  maxChatRetries,
  exitRequest,
  historyRequest = 0,
  onHistoryViewChange,
  onCreateModeChange,
  initialView = 'home',
  previewOpen = false,
  onAppDraftUpdated,
  initialConversation,
  portalTarget,
  portalPreviewOpen,
  onPortalDraftUpdated,
}: ChatPageProps) {
  const navigate = useNavigate();
  const [planMode, setPlanMode] = useState(false);
  const [previewSelection, setPreviewSelection] = useState<PreviewSelectionItem[]>([]);
  const previewSelectionRef = useRef(previewSelection);
  previewSelectionRef.current = previewSelection;
  const isSendingRef = useRef(false);
  const sendMessageRef = useRef<
    ((event?: FormEvent, includeSelection?: boolean) => Promise<void>) | undefined
  >(undefined);
  useEffect(() => {
    // Ctrl+Enter sends the queued selections, from the preview or from anywhere outside the composer.
    const send = () => void sendMessageRef.current?.(undefined, true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Enter' ||
        !(event.ctrlKey || event.metaKey) ||
        event.defaultPrevented ||
        !previewSelectionRef.current.length ||
        (event.target instanceof Element && event.target.closest('form'))
      )
        return;
      event.preventDefault();
      send();
    };
    window.addEventListener('fabrik:preview-selection-send', send);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('fabrik:preview-selection-send', send);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  useEffect(() => {
    const onSelection = (event: Event) => {
      const items = (event as CustomEvent<PreviewSelectionItem[]>).detail;
      setPreviewSelection(Array.isArray(items) ? items : []);
    };
    const onClear = () => setPreviewSelection([]);
    window.addEventListener('fabrik:preview-selection', onSelection);
    window.addEventListener('fabrik:preview-selection-clear', onClear);
    return () => {
      window.removeEventListener('fabrik:preview-selection', onSelection);
      window.removeEventListener('fabrik:preview-selection-clear', onClear);
    };
  }, []);
  useEffect(() => {
    setPreviewSelection([]);
  }, [appId]);
  const chat = useChat(
    tenant,
    appId,
    onPortalDraftUpdated || onAppDraftUpdated,
    (createdAppId, conversation) =>
      navigate(
        `/t/${encodeURIComponent(tenant.tenant_id)}/apps/${encodeURIComponent(createdAppId)}`,
        { state: { conversation } }
      ),
    maxChatRetries,
    planMode
  );
  // Images upload as soon as they are attached, so a new chat needs its id before the first send.
  // A created conversation uses the draft id; clearing the selection starts a fresh draft.
  const selectedConversationId = chat.selected?.id;
  const draftIdRef = useRef('');
  if (selectedConversationId) draftIdRef.current = '';
  else if (!draftIdRef.current) draftIdRef.current = crypto.randomUUID();
  const draftConversationId = draftIdRef.current;
  const attachmentsConversationId = selectedConversationId ?? draftConversationId;
  const attachments = useAttachments(tenant.tenant_id, attachmentsConversationId, (message) =>
    chat.handleError(new Error(message))
  );
  // Files already sent; re-read on submit, first stream chunk (message saved) and turn end.
  const conversationFiles = useConversationFiles(
    tenant.tenant_id,
    selectedConversationId,
    chat.status,
    (message) => chat.handleError(new Error(message)),
    () => void refreshSelectedMessages()
  );
  const [implementationSteps, setImplementationSteps] = useState<Plan['implementationSteps']>([]);
  const [workflowPlan, setWorkflowPlan] = useState<{
    appId: string;
    state: PlanState;
  } | null>(null);
  const [approvingPlan, setApprovingPlan] = useState(false);
  const {
    openConversation,
    setConversationAppId,
    loadConversations,
    refreshSelectedMessages,
    handleError: handleChatError,
    stopAll: stopChat,
  } = chat;
  useEffect(() => {
    const onSelectionError = (event: Event) => {
      const message = (event as CustomEvent<string>).detail;
      handleChatError(new Error(message || 'Could not send the preview selection.'));
    };
    window.addEventListener('fabrik:preview-selection-error', onSelectionError);
    return () => window.removeEventListener('fabrik:preview-selection-error', onSelectionError);
  }, [handleChatError]);

  const answerChatQuestion = async (answers: AskQuestionAnswer[]) => {
    const approvalAnswer = answers.find((answer) => answer.answer === 'approval:approve');
    const approvalQuestion = approvalAnswer
      ? chat.messages
          .flatMap((message) => message.parts)
          .find(
            (part) =>
              part.type === 'data-ask-question' && part.data.id === approvalAnswer.question_id
          )
      : undefined;
    const approval =
      approvalQuestion?.type === 'data-ask-question'
        ? approvalQuestion.data.workflowApproval
        : undefined;

    if (approval) {
      if (approval.appId !== appId) throw new Error('This approval belongs to another app.');
      let latest = await getPlan(tenant.tenant_id, approval.appId);
      if (approval.action === 'approve_phase') {
        if (!approval.nextPhase) throw new Error('The approval phase is unavailable.');
        if (latest.phase !== approval.nextPhase) {
          if (latest.phase !== 'planning' || latest.revision !== approval.expectedRevision) {
            throw new Error('The plan changed. Reload the latest plan and ask for approval again.');
          }
          latest = await approvePlan(
            tenant.tenant_id,
            approval.appId,
            approval.expectedRevision,
            approval.nextPhase
          );
        }
      } else if (approval.action === 'approve_scopes') {
        if (latest.phase !== 'discovery' || !latest.discoveryScopesApproved) {
          if (latest.phase !== 'discovery' || latest.revision !== approval.expectedRevision) {
            throw new Error('The discovery scope changed. Ask for approval again.');
          }
          latest = await approveDiscoveryScopes(
            tenant.tenant_id,
            approval.appId,
            approval.expectedRevision
          );
        }
      } else if (approval.action === 'approve_discovery' && latest.phase !== 'implementation') {
        if (latest.phase !== 'discovery') throw new Error('The discovery phase has changed.');
        if (!latest.discoveryApproved) {
          if (latest.revision !== approval.expectedRevision) {
            throw new Error('The discovery findings changed. Ask for approval again.');
          }
          latest = await approveDiscovery(
            tenant.tenant_id,
            approval.appId,
            approval.expectedRevision
          );
        }
        if (latest.phase !== 'implementation') {
          if (latest.revision === null) throw new Error('The plan revision is unavailable.');
          latest = await approvePlan(
            tenant.tenant_id,
            approval.appId,
            latest.revision,
            'implementation'
          );
        }
      } else if (approval.action !== 'approve_discovery') {
        throw new Error('This approval action is no longer available.');
      }
      window.dispatchEvent(
        new CustomEvent('fabrik:app-plan-updated', {
          detail: { appId: approval.appId, revision: latest.revision },
        })
      );
    }
    await chat.answerQuestion(answers);
  };

  useEffect(() => {
    let controller: AbortController | undefined;
    let latestRevision = 0;
    let minimumRevision = 0;
    const refresh = (updatedRevision?: number) => {
      if (!appId) return;
      if (updatedRevision !== undefined) {
        minimumRevision = Math.max(minimumRevision, updatedRevision);
      }
      controller?.abort();
      controller = new AbortController();
      const { signal } = controller;
      const requestedRevision = minimumRevision;
      const request = requestedRevision
        ? getPlanAtRevision(tenant.tenant_id, appId, requestedRevision, signal)
        : getPlan(tenant.tenant_id, appId, signal);
      void request
        .then((state) => {
          if (signal.aborted) return;
          const revision = state.revision ?? 0;
          if (revision < requestedRevision || revision < latestRevision) return;
          latestRevision = revision;
          setWorkflowPlan({ appId, state });
          setImplementationSteps(
            state.phase === 'implementation' ? state.plan?.implementationSteps ?? [] : []
          );
        })
        .catch(() => undefined);
    };
    if (appId) void refresh();
    else {
      setWorkflowPlan(null);
      setImplementationSteps([]);
    }
    const onPlanUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ appId?: unknown; revision?: unknown }>).detail;
      if (detail?.appId === appId && typeof detail.revision === 'number') {
        void refresh(detail.revision);
      }
    };
    window.addEventListener('fabrik:app-plan-updated', onPlanUpdated);
    return () => {
      controller?.abort();
      window.removeEventListener('fabrik:app-plan-updated', onPlanUpdated);
    };
  }, [appId, tenant.tenant_id]);

  const [view, setView] = useState<'home' | 'interview'>(initialView);
  const [historyView, setHistoryView] = useState(false);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);
  const [questionResetSignal, setQuestionResetSignal] = useState(0);
  const [pageFocused, setPageFocused] = useState(
    () => document.hasFocus() && document.visibilityState === 'visible'
  );
  const refreshOnRefocus = useRef(!pageFocused);
  const originalFaviconHref = useRef<string | undefined>(undefined);
  const notifiedActivities = useRef(new Map<string, AttentionActivity | 'streaming'>());
  const lastExitRequest = useRef(exitRequest);
  const lastHistoryRequest = useRef(historyRequest);
  const lastAppId = useRef(appId);
  const currentAppId = useRef(appId);
  const lastInitialView = useRef(initialView);
  useLayoutEffect(() => {
    currentAppId.current = appId;
  }, [appId]);
  const setTabNotificationIcon = useCallback((show: boolean) => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!favicon) return;
    originalFaviconHref.current ??= favicon.href;
    favicon.href = show ? '/favicon-notification.svg' : originalFaviconHref.current;
  }, []);
  const openSavedConversation = useCallback(
    async (conversation: Conversation) => {
      if (await openConversation(conversation)) {
        setView('interview');
        setHistoryView(false);
        setHistoryLoadFailed(false);
      }
    },
    [openConversation]
  );

  useEffect(() => {
    window.addEventListener('pointerdown', unlockChatAttentionSound);
    window.addEventListener('keydown', unlockChatAttentionSound);
    return () => {
      window.removeEventListener('pointerdown', unlockChatAttentionSound);
      window.removeEventListener('keydown', unlockChatAttentionSound);
    };
  }, []);

  useEffect(() => {
    const updatePageFocus = () => {
      const focused = document.hasFocus() && document.visibilityState === 'visible';
      setPageFocused(focused);
      if (focused) setTabNotificationIcon(false);
    };
    window.addEventListener('focus', updatePageFocus);
    window.addEventListener('blur', updatePageFocus);
    document.addEventListener('visibilitychange', updatePageFocus);
    return () => {
      window.removeEventListener('focus', updatePageFocus);
      window.removeEventListener('blur', updatePageFocus);
      document.removeEventListener('visibilitychange', updatePageFocus);
      setTabNotificationIcon(false);
    };
  }, [setTabNotificationIcon]);

  useEffect(() => {
    if (!pageFocused) {
      refreshOnRefocus.current = true;
      return;
    }
    if (!refreshOnRefocus.current || chat.busy || chat.loading || !chat.selected?.id) return;
    refreshOnRefocus.current = false;
    void refreshSelectedMessages();
  }, [pageFocused, chat.busy, chat.loading, chat.selected?.id, refreshSelectedMessages]);

  useEffect(() => {
    for (const [conversationId, activity] of chat.conversationActivities) {
      const toastId = `conversation-${conversationId}`;
      if (activity === 'streaming') {
        notifiedActivities.current.delete(conversationId);
        toast.close(toastId);
        continue;
      }
      const isSelectedConversation = conversationId === chat.selected?.id;
      const isFocused = document.hasFocus() && document.visibilityState === 'visible';
      if (isSelectedConversation && isFocused) {
        toast.close(toastId);
        continue;
      }
      if (notifiedActivities.current.get(conversationId) === activity) {
        if (isSelectedConversation) toast.close(toastId);
        continue;
      }

      notifiedActivities.current.set(conversationId, activity);
      if (!isFocused) setTabNotificationIcon(true);
      if (isChatAttentionSoundEnabled()) playChatAttentionSound();
      if (isSelectedConversation) {
        toast.close(toastId);
        continue;
      }
      const responsePreview = chat.conversationPreviews.get(conversationId);
      const conversation = chat.conversations.find((item) => item.id === conversationId);
      toast.add({
        id: toastId,
        type: activity === 'waiting-for-input' ? 'warning' : 'success',
        title:
          activity === 'waiting-for-input'
            ? 'Waiting for your inputs'
            : 'A response is waiting for you',
        description:
          responsePreview ||
          (activity === 'waiting-for-input'
            ? 'The assistant is waiting for your input.'
            : 'The assistant has completed a response.'),
        data: { attentionSound: true },
        timeout: 8000,
        actionProps: {
          children: 'Open chat',
          onClick: async () => {
            toast.close(toastId);
            const targetConversation = conversation ?? {
              id: conversationId,
              title: 'Conversation',
              app_id: appId || null,
            };
            if (currentAppId.current !== appId) return;
            try {
              await openSavedConversation(targetConversation);
            } catch (error) {
              handleChatError(error);
            }
          },
        },
      });
    }
  }, [
    chat.conversationActivities,
    chat.conversationPreviews,
    chat.conversations,
    chat.selected?.id,
    appId,
    openSavedConversation,
    handleChatError,
    pageFocused,
    setTabNotificationIcon,
  ]);

  useEffect(() => {
    const appChanged = lastAppId.current !== appId;
    const viewChanged = lastInitialView.current !== initialView;
    if (appChanged) {
      for (const conversationId of notifiedActivities.current.keys()) {
        toast.close(`conversation-${conversationId}`);
      }
      notifiedActivities.current.clear();
    }
    if (lastAppId.current && appChanged) stopChat();
    if (appChanged || viewChanged) {
      setView(initialView);
      setHistoryView(false);
      setHistoryLoadFailed(false);
    }
    lastAppId.current = appId;
    lastInitialView.current = initialView;
  }, [appId, initialView, stopChat]);

  useEffect(() => {
    if (initialConversation) {
      void openConversation(initialConversation);
    } else if (initialView === 'interview') {
      void openConversation(null);
    }
  }, [appId, initialConversation, initialView, openConversation]);

  useEffect(() => {
    setConversationAppId(appId || null);
    if (appId) void loadConversations().catch(handleChatError);
  }, [appId, handleChatError, loadConversations, setConversationAppId]);

  useEffect(() => {
    if (historyRequest === lastHistoryRequest.current) return;
    lastHistoryRequest.current = historyRequest;
    if (historyView) {
      setHistoryView(false);
      setHistoryLoadFailed(false);
      return;
    }
    setView('interview');
    setConversationAppId(appId || null);
    setHistoryLoadFailed(false);
    setHistoryView(true);
    void loadConversations(0).catch(() => setHistoryLoadFailed(true));
  }, [appId, historyRequest, historyView, loadConversations, setConversationAppId]);

  useEffect(() => {
    onHistoryViewChange?.(historyView);
  }, [historyView, onHistoryViewChange]);

  useEffect(() => {
    if (exitRequest === lastExitRequest.current) return;
    lastExitRequest.current = exitRequest;
    setQuestionResetSignal((current) => current + 1);
    void openConversation(null);
    setView('home');
    setHistoryView(false);
    setHistoryLoadFailed(false);
    onCreateModeChange(false);
  }, [exitRequest, onCreateModeChange, openConversation]);

  useEffect(() => () => onCreateModeChange(false), [onCreateModeChange]);

  const openInterview = () => {
    setPlanMode(false);
    void openConversation(null);
    setHistoryView(false);
    setHistoryLoadFailed(false);
    setView('interview');
    onCreateModeChange(true);
  };

  const openBuilder = () => {
    navigate(`/t/${encodeURIComponent(tenant.tenant_id)}/apps/new?mode=canvas`);
  };

  const openSavedApp = (savedAppId: string) => {
    navigate(`/t/${encodeURIComponent(tenant.tenant_id)}/apps/${encodeURIComponent(savedAppId)}`);
  };

  const sendMessage = async (event?: FormEvent, includeSelection = false) => {
    event?.preventDefault();
    // Reading the preview takes a moment; ignore a second send (double Enter) while it runs.
    if (isSendingRef.current) return;
    const typed = chat.query.trim();
    // includeSelection: the preview just confirmed a selection, which this render may not know yet.
    const hasSelection = includeSelection || previewSelectionRef.current.length > 0;
    const hasAttachments = attachments.attachments.length > 0;
    if ((!typed && !hasSelection && !hasAttachments) || attachments.blocking) return;
    isSendingRef.current = true;
    let accepted = false;
    try {
      const selection = hasSelection ? boundSelection(await requestPreviewSnapshot()) : [];
      // A message is optional when selections are queued: their comments carry the request.
      if (!typed && !selection.length && !hasAttachments) return;
      await chat.sendMessage({
        messageText: typed ? undefined : '',
        selection,
        attachments: attachments.ready,
        retryConversationId: draftConversationId || undefined,
        onAccepted: () => {
          accepted = true;
          isSendingRef.current = false;
          attachments.clear();
          // The selection travels with the message (and its retries), and sending finishes the
          // selection task, so release the preview and leave selection mode.
          if (selection.length) {
            setPreviewSelection([]);
            window.dispatchEvent(new CustomEvent('fabrik:preview-selection-clear'));
          }
          window.dispatchEvent(
            new CustomEvent('fabrik:preview-selection-toggle', { detail: { enabled: false } })
          );
        },
      });
    } catch (error) {
      chat.handleError(error);
    } finally {
      if (!accepted) isSendingRef.current = false;
    }
  };

  sendMessageRef.current = sendMessage;

  const activeWorkflowPlan =
    workflowPlan && workflowPlan.appId === appId ? workflowPlan.state : null;
  const nextPlanPhase: 'discovery' | 'implementation' | null =
    activeWorkflowPlan?.plan &&
    activeWorkflowPlan.revision !== null &&
    activeWorkflowPlan.revision > 0
      ? activeWorkflowPlan.phase === 'planning'
        ? activeWorkflowPlan.plan.discoveryScopes?.length
          ? 'discovery'
          : 'implementation'
        : activeWorkflowPlan.phase === 'discovery'
        ? 'implementation'
        : null
      : null;
  const approveNextPlanPhase = async () => {
    if (!appId || !activeWorkflowPlan || !nextPlanPhase || activeWorkflowPlan.revision === null)
      return;
    setApprovingPlan(true);
    try {
      let approved = activeWorkflowPlan;
      if (approved.phase === 'discovery' && !approved.discoveryApproved) {
        if (approved.revision === null) throw new Error('The plan revision is unavailable.');
        approved = await approveDiscovery(tenant.tenant_id, appId, approved.revision);
        setWorkflowPlan({ appId, state: approved });
      }
      if (approved.phase !== nextPlanPhase) {
        if (approved.revision === null) throw new Error('The plan revision is unavailable.');
        approved = await approvePlan(tenant.tenant_id, appId, approved.revision, nextPlanPhase);
      }
      setWorkflowPlan({ appId, state: approved });
      window.dispatchEvent(
        new CustomEvent('fabrik:app-plan-updated', {
          detail: { appId, revision: approved.revision },
        })
      );
      await chat.sendText(
        `Continue with the approved ${
          nextPlanPhase === 'discovery' ? 'Discovery' : 'Implementation'
        } phase.`
      );
    } catch (error) {
      handleChatError(error);
    } finally {
      setApprovingPlan(false);
    }
  };

  const showConversationHistory = () => {
    setConversationAppId(appId || null);
    setHistoryLoadFailed(false);
    setHistoryView(true);
    void loadConversations(0).catch(() => setHistoryLoadFailed(true));
  };

  const startNewConversation = () => {
    setPlanMode(false);
    void openConversation(null);
    setHistoryView(false);
    setHistoryLoadFailed(false);
  };

  if (view === 'home') {
    return (
      <FabrikHome tenant={tenant} user={user} onCreate={openInterview} onOpenApp={openSavedApp} />
    );
  }

  const showWelcome =
    !chat.planRequired &&
    !chat.error &&
    !chat.quotaExhausted &&
    !chat.canRetry &&
    !chat.loading &&
    !chat.messages.length &&
    !chat.busy;
  const queuedMessages = chat.queuedMessages.filter((message) => message.status !== 'steering');
  const steeringMessage = chat.dispatchingMessage
    ? undefined
    : chat.queuedMessages.find((message) => message.status === 'steering');
  const renderComposer = (placeholder?: string) => (
    <ChatComposer
      value={chat.query}
      busy={chat.busy}
      loading={chat.loading}
      quotaExhausted={chat.quotaExhausted}
      model={chat.model}
      onModelChange={chat.setModel}
      onChange={chat.setQuery}
      onSend={sendMessage}
      previewSelection={previewSelection}
      previewSelectionToolAvailable={Boolean(portalTarget && (portalPreviewOpen ?? previewOpen))}
      showSelectionButton={Boolean(appId)}
      attachments={attachments.attachments}
      attachmentsBlocking={attachments.blocking}
      onAttach={attachments.add}
      onRemoveAttachment={attachments.remove}
      onRetryAttachment={attachments.retry}
      conversationFiles={conversationFiles.files}
      onClearPreviewSelection={() => {
        setPreviewSelection([]);
        window.dispatchEvent(new CustomEvent('fabrik:preview-selection-clear'));
      }}
      onStop={() => {
        setQuestionResetSignal((current) => current + 1);
        chat.stop();
      }}
      placeholder={placeholder}
      welcome={showWelcome}
      showPlanModeToggle={!appId && view === 'interview' && !chat.selected && !historyView}
      planMode={planMode}
      onPlanModeChange={() => setPlanMode((selected) => !selected)}
    />
  );
  const composer =
    nextPlanPhase && activeWorkflowPlan && !chat.busy ? (
      <section
        className="mx-auto mt-1 w-full max-w-4xl min-w-0 @container rounded-xl border border-app-border-strong bg-app-surface-card p-3 text-left"
        aria-label={activeWorkflowPlan.phase === 'discovery' ? 'Discovery review' : 'Plan review'}
        aria-busy={approvingPlan}
      >
        <div className="flex items-start gap-2.5">
          {!activeWorkflowPlan.blockers.length && (
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-app-brand-subtle text-app-brand-subtle-foreground">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
            <h2 className="min-w-0 text-sm font-semibold text-app-text">
              {activeWorkflowPlan.blockers.length
                ? activeWorkflowPlan.phase === 'discovery'
                  ? 'Discovery needs a few details'
                  : 'Plan needs a few details'
                : activeWorkflowPlan.phase === 'discovery'
                ? 'Discovery ready for review'
                : 'Plan ready for review'}
            </h2>
            <Button
              type="button"
              size="sm"
              className="bg-app-action text-app-action-foreground hover:bg-app-action-hover"
              disabled={
                activeWorkflowPlan.blockers.length > 0 ||
                approvingPlan ||
                chat.busy ||
                chat.loading ||
                chat.quotaExhausted ||
                Boolean(chat.query.trim())
              }
              onClick={() => void approveNextPlanPhase()}
            >
              {approvingPlan ? 'Starting…' : 'Approve'}
            </Button>
          </div>
        </div>
        {activeWorkflowPlan.blockers.length ? (
          <div className="mb-3 mt-1 text-sm leading-5 text-app-text-secondary">
            <p>Complete these requirements before approval:</p>
            <ul className="mt-1 list-disc pl-5">
              {activeWorkflowPlan.blockers.map((blocker) => (
                <li key={blocker}>{workflowBlockerLabels[blocker] ?? blocker}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mb-3 mt-1 text-sm leading-5 text-app-text-secondary">
            {`Approve to begin ${
              nextPlanPhase === 'discovery' ? 'Discovery' : 'Implementation'
            }, or describe a change below.`}
          </p>
        )}
        {renderComposer(
          activeWorkflowPlan.phase === 'discovery'
            ? 'Describe what you would like changed in the plan or discovery findings…'
            : 'Describe what you would like changed in the plan…'
        )}
      </section>
    ) : (
      renderComposer()
    );
  const isPreviewOpen = portalPreviewOpen ?? previewOpen;
  const composerSlotClassName = `mx-auto w-full max-w-5xl shrink-0 px-7 pb-8 @max-5xl:mx-0 @max-5xl:max-w-none ${
    isPreviewOpen
      ? '@max-5xl:px-3 @max-5xl:py-2'
      : '@max-5xl:border-t @max-5xl:border-app-border @max-5xl:bg-app-surface-card @max-5xl:px-3 @max-5xl:py-2'
  }`;

  const page = (
    <main className="@container relative flex min-h-0 min-w-0 flex-1 flex-col bg-app-surface-muted">
      {appId && (
        <ChatHeader
          title={chat.selected?.title}
          historyView={historyView}
          canStartNew={historyView || !showWelcome}
          previewOpen={isPreviewOpen}
          conversationFiles={conversationFiles.files}
          onToggleFile={conversationFiles.toggle}
          onDeleteFile={conversationFiles.remove}
          onHistoryToggle={() => (historyView ? setHistoryView(false) : showConversationHistory())}
          onNewConversation={startNewConversation}
        />
      )}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface-muted">
        {historyView ? (
          <ChatHistory
            conversations={chat.conversations}
            conversationActivities={chat.conversationActivities}
            loading={chat.conversationsLoading}
            total={chat.total}
            loadFailed={historyLoadFailed}
            onRetry={showConversationHistory}
            onLoadMore={() => void chat.loadMoreConversations()}
            onOpenConversation={(conversation) => void openSavedConversation(conversation)}
            onBackToAssistant={() => setHistoryView(false)}
          />
        ) : chat.planRequired ? (
          <Alert className="mx-auto mt-8 w-full max-w-5xl border-app-warning-border bg-app-warning-surface text-xs text-app-warning">
            <AlertDescription className="text-xs text-app-warning">{chat.error}</AlertDescription>
          </Alert>
        ) : showWelcome ? (
          <ChatWelcome
            composer={composer}
            onSelectExample={chat.setQuery}
            onOpenBuilder={appId ? undefined : openBuilder}
          />
        ) : (
          <>
            <ChatConversation
              messages={chat.messages}
              dispatchingMessage={chat.dispatchingMessage}
              savedMessages={chat.savedMessages}
              steeringMessage={steeringMessage}
              busy={chat.busy}
              loading={chat.loading}
              hasMoreMessages={Boolean(chat.selected && chat.messages.length < chat.messageTotal)}
              status={chat.status}
              model={chat.model}
              onRate={(message, rating) => void chat.rateMessage(message, rating)}
              onLoadMore={() => void chat.loadMoreMessages()}
            />
            <ChatComposerPanel
              className={composerSlotClassName}
              messages={chat.messages}
              savedMessages={chat.savedMessages}
              busy={chat.busy}
              loading={chat.loading}
              error={chat.error}
              quotaExhausted={chat.quotaExhausted}
              composer={composer}
              queuedMessages={queuedMessages}
              checklist={
                implementationSteps.length > 0 && (
                  <ImplementationChecklist
                    className=""
                    steps={implementationSteps}
                    busy={chat.busy}
                  />
                )
              }
              resetSignal={questionResetSignal}
              retryAttempt={chat.retryAttempt}
              maxRetries={chat.maxRetries}
              canRetry={chat.canRetry}
              hideWorkflowApprovalQuestion={Boolean(nextPlanPhase && activeWorkflowPlan)}
              onRetry={chat.retryLast}
              onDismissError={chat.dismissError}
              onAnswer={answerChatQuestion}
              onAnswerError={handleChatError}
              onSteer={(id) => void chat.steerQueuedMessage(id)}
              onRemoveQueued={chat.removeQueuedMessage}
            />
          </>
        )}
      </section>
      <FeedbackDialog
        feedback={chat.feedback}
        categories={chat.categories}
        saving={chat.saving}
        onSave={(rating, category, comment) => void chat.submitFeedback(rating, category, comment)}
        onClose={() => chat.setFeedback(null)}
      />
    </main>
  );
  return portalTarget ? createPortal(page, portalTarget) : page;
}
